import CustomTooltip from '../../shared/CustomTooltip';
import EditIcon from '@mui/icons-material/Edit';
import {
    Card,
    CardActionArea,
    Chip,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetPolicyListQuery } from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import { camelCaseToTitleCase, capitalizeName } from '../../../shared/utils';
import { ExperienceType } from '../../enums/experience';
import { Policy } from '../../types/policy';
import { PolicySortKey, PolicyTypeFilter } from './PolicyToolbar';

export default function PolicyList({
    query,
    typeFilter,
    expFilter,
    sortKey,
}: {
    query: string;
    typeFilter: PolicyTypeFilter;
    expFilter: ExperienceType[];
    sortKey: PolicySortKey;
}) {
    const navigate = useNavigate();
    const loggedInUser = UserRole();
    const { data: policies } = useGetPolicyListQuery(loggedInUser?.company);
    const allPolicies: Policy[] = policies ?? [];

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        let rows = allPolicies.filter((p) => {
            const matchesQ =
                !q ||
                p.name.toLowerCase().includes(q) ||
                p.description?.toLowerCase().includes(q) ||
                p.cancellationType?.toLowerCase().includes(q);

            // Type filter driven by cancellationType (e.g., 'fair', 'strict', etc.)
            const matchesType =
                typeFilter === 'All' ||
                p.cancellationType?.toLowerCase() === typeFilter.toLowerCase();

            // Experience filter: if expFilter empty => all; if policy applies to all (moduleTypes empty) => include;
            // otherwise require at least one overlap.
            const matchesExp =
                expFilter.length === 0 ||
                (p.moduleTypes?.length ?? 0) === 0 ||
                p.moduleTypes?.some((t) =>
                    expFilter.includes(t as ExperienceType),
                );

            return matchesQ && matchesType && matchesExp;
        });

        rows = rows.sort((a, b) => {
            const aTime = safeTime(a.createdAt);
            const bTime = safeTime(b.createdAt);

            if (sortKey === 'nameAsc') return a.name.localeCompare(b.name);
            if (sortKey === 'nameDesc') return b.name.localeCompare(a.name);
            if (sortKey === 'createdDesc') return bTime - aTime;
            if (sortKey === 'createdAsc') return aTime - bTime;
            return 0;
        });

        return rows;
    }, [allPolicies, query, typeFilter, expFilter, sortKey]);

    if (filtered.length === 0) {
        return (
            <Typography sx={{ mt: 3 }}>
                No policies. Try adjusting your filters.
            </Typography>
        );
    }

    return (
        <Stack spacing={1.5} sx={{ width: '100%' }}>
            {filtered.map((policy) => (
                <PolicyRow
                    key={policy.id}
                    policy={policy}
                    onClick={() => navigate(`/policies/${policy.id}`)}
                />
            ))}
        </Stack>
    );
}

function PolicyRow({
    policy,
    onClick,
}: {
    policy: Policy;
    onClick: () => void;
}) {
    const theme = useTheme();

    return (
        <Card
            variant='outlined'
            sx={{
                borderRadius: 2,
                '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
            }}
            onClick={onClick}
        >
            <CardActionArea sx={{ px: 1.5, py: 1.5 }}>
                <Stack
                    direction='row'
                    alignItems='center'
                    spacing={2}
                    sx={{ width: '100%' }}
                >
                    {/* Left: name + created date */}
                    <Stack sx={{ minWidth: 220 }}>
                        <Typography variant='caption' sx={{ color: 'primary' }}>
                            {capitalizeName(policy?.name)}
                        </Typography>
                        <Typography variant='body3' color='text.primary'>
                            {policy?.createdAt
                                ? `Created ${moment(policy.createdAt).format(
                                      'DD/MM/YYYY',
                                  )}`
                                : 'No date'}
                        </Typography>
                    </Stack>

                    {/* Middle meta: cancellation type + experiences */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ minWidth: 280, overflow: 'hidden' }}
                    >
                        {policy.cancellationType && (
                            <Chip
                                label={`${camelCaseToTitleCase(
                                    policy.cancellationType,
                                )} Cancellation Policy`}
                                variant='outlined'
                                sx={{
                                    minWidth: 250,
                                    justifyContent: 'center',
                                    bgcolor: theme.palette.neutral.main, // keeps white background
                                    borderColor:
                                        policy.cancellationType === 'flexible'
                                            ? theme.palette.policy.flexible
                                            : policy.cancellationType === 'fair'
                                              ? theme.palette.policy.fair
                                              : policy.cancellationType ===
                                                  'firm'
                                                ? theme.palette.policy.firm
                                                : theme.palette.policy
                                                      .nonRefundable,
                                    color:
                                        policy.cancellationType === 'flexible'
                                            ? theme.palette.policy.flexible
                                            : policy.cancellationType === 'fair'
                                              ? theme.palette.policy.fair
                                              : policy.cancellationType ===
                                                  'firm'
                                                ? theme.palette.policy.firm
                                                : theme.palette.policy
                                                      .nonRefundable,
                                }}
                            />
                        )}
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ overflow: 'hidden' }}
                    >
                        <Typography variant='body3' color='text.primary' noWrap>
                            Policy Id #{policy.id} {''}|
                        </Typography>
                    </Stack>
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
                    >
                        <Typography variant='body3' color='text.primary' noWrap>
                            Applies to:&nbsp;
                            {policy?.moduleTypes?.length === 0
                                ? 'All'
                                : policy?.moduleTypes
                                      .map((t) => capitalizeName(String(t)))
                                      .join(', ')}
                        </Typography>
                    </Stack>

                    {/* Right actions */}
                    <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        onClick={(e) => e.stopPropagation()}
                    >
                        <CustomTooltip title='Edit'>
                            <IconButton size='small' onClick={onClick}>
                                <EditIcon fontSize='small' />
                            </IconButton>
                        </CustomTooltip>
                    </Stack>
                </Stack>
            </CardActionArea>
        </Card>
    );
}

// --- helper for safe date parsing ---
function safeTime(d?: string | Date): number {
    return d ? new Date(d).getTime() : 0;
}
